/**
 * Client-side image compressor.
 * Downscales overly-large photos before upload so property pages load fast
 * and object-storage costs stay in check.
 *
 * Rules:
 *  - Skips files < 300 KB (already small)
 *  - Skips GIFs (may be animated)
 *  - Downscales so max(width, height) <= MAX_DIM (default 2000 px)
 *  - Re-encodes as JPEG @ quality 0.82 for JPGs/HEIC-like; keeps PNG if alpha likely
 *  - Preserves original if compression somehow makes it larger
 *
 * Returns a Promise<File> — always safe to feed into FormData.
 */

const SKIP_UNDER_BYTES = 300 * 1024; // 300 KB
const MAX_DIM = 2000;
const JPEG_QUALITY = 0.82;

// Types we intentionally leave alone (server accepts them anyway).
const PASSTHROUGH_TYPES = new Set(["image/gif"]);

function loadImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
        img.src = url;
    });
}

function canvasToFile(canvas, mime, quality, baseName) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) return reject(new Error("Canvas toBlob failed"));
                const ext = mime === "image/png" ? "png" : "jpg";
                const cleanName = (baseName || "image").replace(/\.[^.]+$/, "");
                resolve(new File([blob], `${cleanName}.${ext}`, { type: mime, lastModified: Date.now() }));
            },
            mime,
            quality
        );
    });
}

export async function compressImage(file, opts = {}) {
    if (!file || !file.type?.startsWith("image/")) return file;
    if (PASSTHROUGH_TYPES.has(file.type)) return file;
    if (file.size <= SKIP_UNDER_BYTES) return file;

    const maxDim = opts.maxDim ?? MAX_DIM;
    const quality = opts.quality ?? JPEG_QUALITY;

    let img;
    try { img = await loadImage(file); }
    catch { return file; } // don't block upload if decoding fails

    const { naturalWidth: w, naturalHeight: h } = img;
    if (!w || !h) return file;

    const scale = Math.min(1, maxDim / Math.max(w, h));
    const targetW = Math.round(w * scale);
    const targetH = Math.round(h * scale);
    // If already within max-dim AND file is < 1.5 MB, skip re-encoding.
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file;

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, targetW, targetH);

    // PNGs with transparency we keep as PNG; everything else -> JPEG (smaller).
    const outMime = file.type === "image/png" ? "image/png" : "image/jpeg";
    const outQuality = outMime === "image/jpeg" ? quality : undefined;

    let compressed;
    try { compressed = await canvasToFile(canvas, outMime, outQuality, file.name); }
    catch { return file; }

    // Never ship a compressed file that's larger than the original.
    return compressed.size < file.size ? compressed : file;
}
